<template>
  <div>
    <p class="text-muted">
      {{ intro }}
    </p>

    <button class="btn btn-primary mb-3" :disabled="refreshing" @click="refresh">
      {{ refreshing ? 'Buscando...' : (hasSearched ? 'Atualizar' : 'Buscar Dispositivos Órfãos') }}
    </button>

    <div v-if="hasSearched" class="orphan-filters form-row align-items-end mb-2">
      <div class="col-md-4 mb-1">
        <label class="mb-0 small text-muted">Buscar</label>
        <b-input-group size="sm">
          <b-input-group-prepend is-text>
            <i class="fas fa-search" />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchDraft"
            type="search"
            :placeholder="isMovias ? 'Placa, serial ou descrição' : 'IMEI, MAC, Android ID ou descrição'"
            @keyup.enter="applySearch"
          />
          <b-input-group-append>
            <b-button variant="outline-primary" @click="applySearch">
              Pesquisar
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
      <div v-if="isMovias" class="col-md-auto mb-1">
        <label class="mb-0 small text-muted d-block">Sugestão pela placa do Movias</label>
        <b-form-radio-group
          v-model="filters.suggested"
          :options="suggestedOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
        />
      </div>
      <template v-else>
        <div class="col-md-2 mb-1">
          <label class="mb-0 small text-muted">Tipo</label>
          <b-form-select v-model="filters.kind" :options="kindOptions" size="sm" />
        </div>
        <div class="col-md-2 mb-1">
          <label class="mb-0 small text-muted">Posição</label>
          <b-form-select v-model="filters.position" :options="positionOptions" size="sm" />
        </div>
      </template>
      <div class="col text-right mb-2">
        <small class="text-muted">{{ meta.total }} órfão(s)</small>
      </div>
    </div>

    <div v-if="hasSearched && !isLoading && !orphans.length" class="alert alert-secondary mb-0">
      {{ hasFilters ? 'Nenhum órfão com esses filtros.' : `Nenhum dispositivo órfão ${isMovias ? 'do Movias' : 'do app ou de Tags'} encontrado.` }}
    </div>

    <div v-if="orphans.length" class="table-responsive">
      <table class="table table-sm">
        <thead>
          <tr>
            <th>Identificação</th>
            <th>Descrição</th>
            <th>{{ isMovias ? 'Criado em' : 'Visto por último' }}</th>
            <th>Veículo</th>
            <th>Central</th>
            <th>Unidade</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="device in orphans" :key="device.id">
            <td>
              <small class="text-muted d-block">{{ identityLabel(device) }}</small>
              {{ identityOf(device) }}
              <small v-if="device.movias_license_plate" class="text-muted d-block">
                Placa no Movias: {{ device.movias_license_plate }}
              </small>
            </td>
            <td>{{ device.description }}</td>
            <td>{{ isMovias ? createdAt(device) : lastSeen(device) }}</td>
            <td class="orphan-select-cell">
              <v-select
                v-model="formByDevice[device.id].vehicle_id"
                :options="vehicleOptions"
                :reduce="vehicle => vehicle.id"
                label="label"
                placeholder="Pesquise por placa ou nome"
                class="orphan-select"
                append-to-body
                :calculate-position="positionDropdown"
                @input="onVehicleChange(device.id)"
              />
              <small
                v-if="device.suggested_vehicle && formByDevice[device.id].vehicle_id === device.suggested_vehicle.id"
                class="text-success"
              >
                Sugerido pela placa do Movias
              </small>
            </td>
            <td class="orphan-select-cell">
              <v-select
                v-model="formByDevice[device.id].center_id"
                :options="centers"
                :reduce="center => center.id"
                label="name"
                placeholder="Selecione"
                class="orphan-select"
                append-to-body
                :calculate-position="positionDropdown"
                :disabled="!!formByDevice[device.id].vehicle_id"
                @input="onCenterChange(device.id)"
              />
            </td>
            <td class="orphan-select-cell">
              <v-select
                v-model="formByDevice[device.id].unit_id"
                :options="unitsByDevice[device.id] || []"
                :reduce="unit => unit.id"
                label="name"
                placeholder="Selecione"
                class="orphan-select"
                append-to-body
                :calculate-position="positionDropdown"
                :disabled="!formByDevice[device.id].center_id || !!formByDevice[device.id].vehicle_id"
              />
            </td>
            <td class="text-nowrap">
              <button
                class="btn btn-sm btn-outline-primary mr-1"
                :disabled="!device.last_update"
                @click="track(device)"
              >
                Rastrear
              </button>
              <button
                class="btn btn-sm btn-success"
                :disabled="!isComplete(device.id) || adoptingByDevice[device.id]"
                @click="adopt(device)"
              >
                {{ adoptLabel(device) }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <b-pagination
        v-if="meta.last_page > 1"
        :value="meta.current_page"
        :total-rows="meta.total"
        :per-page="meta.per_page"
        size="sm"
        align="center"
        @input="loadOrphans"
      />
    </div>

    <div v-if="tracking" class="mt-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <strong>Rastreando {{ identityOf(tracking) }}</strong>
        <small class="text-muted">
          {{ trackPath.length }} posição(ões) nas últimas 24 h · {{ lastSeen(tracking) }}
        </small>
      </div>
      <GmapMap
        :center="trackCenter"
        :zoom="15"
        style="width: 100%; height: 360px"
      >
        <GmapPolyline
          :path="trackPath"
          :options="{ strokeColor: '#4169e1', strokeOpacity: 0.9, strokeWeight: 4 }"
        />
        <GmapMarker :position="trackCenter" />
      </GmapMap>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import vSelect from 'vue-select'

export default {
  name: 'OrphanDevicesPanel',
  components: { vSelect },
  props: {
    centers: { type: Array, default: () => [] },
    vehicles: { type: Array, default: () => [] },
    origin: { type: String, default: 'other' },
  },
  data() {
    return {
      orphans: [],
      filters: {
        search: '',
        suggested: null,
        kind: null,
        position: null,
      },
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
        counts: { all: 0, suggested: 0, unsuggested: 0 },
      },
      kindOptions: [
        { value: null, text: 'Todos' },
        { value: 'app', text: 'Aplicativo' },
        { value: 'tag', text: 'Tag (GT06)' },
      ],
      positionOptions: [
        { value: null, text: 'Todas' },
        { value: 'recent', text: 'Últimas 24 h' },
        { value: 'none', text: 'Sem posição' },
      ],
      formByDevice: {},
      unitsByDevice: {},
      adoptingByDevice: {},
      isLoading: false,
      refreshing: false,
      searchDraft: '',
      requestSeq: 0,
      hasSearched: false,
      tracking: null,
      trackPath: [],
      echoListening: false,
    }
  },
  computed: {
    vehicleOptions() {
      return this.vehicles.map(v => ({
        id: v.id,
        label: v.name && v.name !== v.license_plate ? `${v.license_plate} — ${v.name}` : v.license_plate,
      }))
    },
    suggestedOptions() {
      const { counts } = this.meta
      return [
        { value: null, text: `Todos (${counts.all})` },
        { value: 'only', text: `Sugeridos (${counts.suggested})` },
        { value: 'none', text: `Sem sugestão (${counts.unsuggested})` },
      ]
    },
    hasFilters() {
      return !!(this.filters.search || this.filters.suggested || this.filters.kind || this.filters.position)
    },
    isMovias() {
      return this.origin === 'movias'
    },
    intro() {
      return this.isMovias
        ? 'Aparelhos que o Movias enviou e que ainda não existem no Frotas. Quando a placa do Movias bate com um veículo, ele já vem sugerido: é só amarrar (central e unidade vêm do veículo).'
        : 'Dispositivos criados pelo aplicativo ou por Tags (GT06) sem vínculo, ainda sem central, unidade e empresa. Escolha o veículo para amarrar direto ou a central e a unidade para adotar.'
    },
    trackCenter() {
      const last = this.trackPath[this.trackPath.length - 1]
      if (last) return last
      const update = this.tracking && this.tracking.last_update
      return update ? { lat: Number(update.lat), lng: Number(update.long) } : { lat: 0, lng: 0 }
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        if (this.hasSearched) this.loadOrphans(1)
      },
    },
    searchDraft(value) {
      if (!value && this.filters.search) this.applySearch()
    },
    vehicles() {
      Object.keys(this.formByDevice)
        .filter(deviceId => this.formByDevice[deviceId].vehicle_id)
        .forEach(deviceId => this.onVehicleChange(deviceId))
    },
  },
  beforeDestroy() {
    this.stopListening()
  },
  methods: {
    identityLabel(device) {
      if (device.imei) return 'IMEI'
      if (device.mac_address) return 'MAC'
      if (device.android_id) return 'Android ID'
      return 'Serial'
    },
    identityOf(device) {
      return device.imei || device.mac_address || device.android_id || device.serial_number || `#${device.id}`
    },
    createdAt(device) {
      return device.created_at ? moment(device.created_at).format('DD/MM/YYYY HH:mm') : '—'
    },
    lastSeen(device) {
      const at = device.last_update && device.last_update.last_tracking_at
      return at ? moment(at).format('DD/MM/YYYY HH:mm:ss') : 'sem posição'
    },
    async track(device) {
      this.tracking = device
      this.trackPath = []
      try {
        const api = `${this.$store.state.api}devices/${device.id}/tracker-history`
        const response = await axios.get(api, {
          params: { from: moment().subtract(24, 'hours').format('YYYY-MM-DD HH:mm:ss') },
        })
        this.trackPath = (response.data.data || []).map(p => ({
          lat: Number(p.latitude),
          lng: Number(p.longitude),
        }))
      } catch (error) {
        this.$error(error)
      }
      this.listen()
    },
    listen() {
      if (this.echoListening || !window.Echo || !window.Echo.channel) return
      window.Echo.channel('device-last-update').listen('.device-last-update', this.onDevicePosition)
      this.echoListening = true
    },
    stopListening() {
      if (!this.echoListening || !window.Echo || !window.Echo.channel) return
      window.Echo.channel('device-last-update').stopListening('.device-last-update', this.onDevicePosition)
      this.echoListening = false
    },
    onDevicePosition(event) {
      const orphan = this.orphans.find(item => item.id === Number(event.device_id))
      if (!orphan) return
      this.$set(orphan, 'last_update', {
        ...(orphan.last_update || {}),
        lat: event.lat,
        long: event.long,
        speed: event.speed,
        last_tracking_at: event.last_tracking_at,
      })
      if (this.tracking && this.tracking.id === orphan.id) {
        this.trackPath.push({ lat: Number(event.lat), lng: Number(event.long) })
      }
    },
    orphanParams(page) {
      const params = { origin: this.isMovias ? 'movias' : 'other', page, per_page: this.meta.per_page }
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key]) params[key] = this.filters[key]
      })
      return params
    },
    positionDropdown(dropdownList, component, { width, top, left }) {
      Object.assign(dropdownList.style, {
        top, left, width, minWidth: '280px', zIndex: 2100,
      })
    },
    applySearch() {
      this.filters.search = (this.searchDraft || '').trim()
    },
    async refresh() {
      this.refreshing = true
      try {
        await this.loadOrphans(this.hasSearched ? this.meta.current_page : 1)
      } finally {
        this.refreshing = false
      }
    },
    async loadOrphans(page = 1) {
      this.requestSeq += 1
      const seq = this.requestSeq
      this.isLoading = true
      try {
        const api = `${this.$store.state.api}devices/orphans`
        const response = await axios.get(api, { params: this.orphanParams(page) })
        if (seq !== this.requestSeq) return
        this.orphans = response.data.data || []
        this.meta = { ...this.meta, ...(response.data.meta || {}) }
        this.formByDevice = {}
        this.unitsByDevice = {}
        this.orphans.forEach(device => {
          this.$set(this.formByDevice, device.id, {
            vehicle_id: device.suggested_vehicle ? device.suggested_vehicle.id : null,
            center_id: null,
            unit_id: null,
          })
          this.onVehicleChange(device.id)
        })
      } catch (error) {
        this.$error(error)
      } finally {
        if (seq === this.requestSeq) {
          this.isLoading = false
          this.hasSearched = true
        }
      }
    },
    onVehicleChange(deviceId) {
      const form = this.formByDevice[deviceId]
      const vehicle = this.vehicles.find(v => Number(v.id) === Number(form.vehicle_id))
      form.center_id = vehicle ? vehicle.center_id || null : null
      this.onCenterChange(deviceId, vehicle ? vehicle.unit_id || null : null)
    },
    async onCenterChange(deviceId, unitId = null) {
      this.formByDevice[deviceId].unit_id = unitId
      const centerId = this.formByDevice[deviceId].center_id
      if (!centerId) {
        this.$set(this.unitsByDevice, deviceId, [])
        return
      }
      try {
        const api = `${this.$store.state.api}centers/unit/${centerId}`
        const response = await axios.get(api)
        this.$set(this.unitsByDevice, deviceId, response.data || [])
      } catch (error) {
        this.$error(error)
      }
    },
    isComplete(deviceId) {
      const form = this.formByDevice[deviceId] || {}
      return form.vehicle_id || (form.center_id && form.unit_id)
    },
    adoptLabel(device) {
      const linking = !!this.formByDevice[device.id].vehicle_id
      if (this.adoptingByDevice[device.id]) return linking ? 'Amarrando...' : 'Adotando...'
      return linking ? 'Amarrar' : 'Adotar'
    },
    adoptPayload(deviceId) {
      const form = this.formByDevice[deviceId]
      return form.vehicle_id ? { vehicle_id: form.vehicle_id } : { center_id: form.center_id, unit_id: form.unit_id }
    },
    async adopt(device) {
      this.$set(this.adoptingByDevice, device.id, true)
      try {
        const api = `${this.$store.state.api}devices/${device.id}/adopt`
        await axios.put(api, this.adoptPayload(device.id))
        this.loadOrphans(this.orphans.length > 1 ? this.meta.current_page : 1)
        this.$message(
          this.$t('Success'),
          'Dispositivo adotado com sucesso.',
          'success',
        )
        this.$emit('adopted')
      } catch (error) {
        this.$error(error)
      } finally {
        this.$set(this.adoptingByDevice, device.id, false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.orphan-select-cell {
  min-width: 180px;
}
</style>
